import { useQuery } from "@tanstack/react-query";

import { getDashboardSummary } from "@/services/api/dashboard";
import { getKb } from "@/services/api/knowledge";
import { queryKeys } from "@/services/api/query-keys";
import { getSupportStats } from "@/services/api/support";

const nf = new Intl.NumberFormat("pt-BR");

export type NavBadges = Record<"nav-kb" | "nav-fila" | "nav-part", string | null>;

/**
 * Porte dos contadores da navegação (`navCounts` em `app/web/js/api.js`):
 *
 * - `nav-kb`: % de documentos da base de conhecimento com status "aprovado"
 *   (`GET /api/kb`).
 * - `nav-fila`: tamanho da fila de atendimento humano (`GET
 *   /api/atendimento/stats`).
 * - `nav-part`: total de credenciados do evento ativo (`GET
 *   /api/dashboard/{id}`).
 *
 * Igual ao legado, qualquer falha numa dessas chamadas só resulta em badge
 * ausente (sem número) — nunca quebra a navegação.
 */
export function useNavBadges(eventId: number | null): NavBadges {
  const kbQuery = useQuery({
    queryKey: queryKeys.knowledge.list,
    queryFn: getKb,
    retry: 0,
    staleTime: 60_000,
  });

  const statsQuery = useQuery({
    queryKey: queryKeys.support.stats,
    queryFn: getSupportStats,
    retry: 0,
    staleTime: 30_000,
  });

  const dashboardQuery = useQuery({
    queryKey: queryKeys.dashboard.summary(eventId ?? "none"),
    queryFn: () => getDashboardSummary(eventId as number),
    enabled: eventId != null,
    retry: 0,
    staleTime: 30_000,
  });

  const kb = kbQuery.data;
  const totalDocs = kb ? (kb.total_documentos || kb.documentos.length) : 0;
  const aprovados = kb ? kb.documentos.filter((d) => /aprovad/i.test(d.status || "")).length : 0;
  const navKb = kb && totalDocs ? `${Math.round((100 * aprovados) / totalDocs)}%` : null;

  const fila = statsQuery.data?.fila;
  const navFila = fila ? nf.format(fila) : null;

  const credenciados = dashboardQuery.data?.credenciados;
  const navPart = credenciados ? nf.format(credenciados) : null;

  return {
    "nav-kb": navKb,
    "nav-fila": navFila,
    "nav-part": navPart,
  };
}
